import { FilterService } from "primereact/api";
import {
    getInspectionAndGradeData,
    getInternalGradeData
} from "../../services/FleetDataService";
import {
    getDistinctItems,
    returnAIGrade,
    toVesselTypeWithCount
} from "../../utils/FleetHelper";
import {
    addCustomFieldFleetFiltersToData,
    mutateFleetDataWithInspections
} from "../../utils/FleetTableDataHelper";
import {
    getUserSessionStorageItem,
    storeUserSessionStorageItem
} from "../../utils/UserSessionStorage";
import {
    selectFleet,
    selectFleetDataItems,
    selectInspectionAndGradeData,
    selectShowStatsTilesPanel
} from "../selectors/fleetSelectors";
import {
    selectUser,
    selectUserDetails,
    selectUserHasCapability,
    selectUserToken
} from "../selectors/userSelectors";
import {
    setDistinctCountriesOfBuild,
    setDistinctFlags,
    setDistinctTechnicalManagers,
    setDistinctVessels,
    setDistinctVesselsWithCount,
    setDistinctVesselTypes,
    setFleetDataItems,
    setInspectionAndGradeData,
    setInternalGradeData,
    setInspectionAndGradeDataError,
    setInspectionAndGradeDataLoading,
    setLoading,
    setMarketDataFilteringOptions,
    setShowStatsTilesPanel,
    setStatTileConfigs
} from "../slices/fleetSlice";
import { AppThunk } from "../store";
import {
    getStoredAdvancedFiltersState,
    setupAdvancedFilters
} from "./advancedFiltersThunks";
import { loadGlobalFilterStateFromLocalStorage } from "./filtersThunks";
import { cloneDeep } from "lodash";
import { FleetTableFields } from "../../components/FleetTable/FleetTableFields";
import { customRankFilter } from "../../components/FleetTable/FilterTemplates";
import { selectDefects } from "../selectors/defectSelectors";
import { selectFleetGraph } from "../selectors/fleetGraphSelectors";
import { calculateFleetStatistics } from "../../utils/calculateStatistics";
import { getInspectionsCompletedInContractYear } from "../../utils/StatsHelper";
import { MAX_VISIBLE_TILES } from "../../utils/Constants";
import { setGraphContextFilter } from "../slices/filtersSlice";
import { InspectionAndGradeDataResponse } from "../../types/InspectionAndGradeDataResponse";
import { InternalGradeData } from "../../types/InternalGradeData";
import { mapScheduledInspections } from "../../utils/ScheduledHelper";
import { FleetDataItem } from "../../types/FleetDataItem";
import { Capability } from "../../types/Capability";
import { sendGAEvent } from "../../AnalyticsService";
import { ANALYTICS_BUTTON_CLICK, FLEET_STATS_TOGGLE_CLICK } from "../../constants/analytics";

export const fetchInspectionAndGradeData =
    (): AppThunk<Promise<boolean>> =>
    async (dispatch, getState) => {
        const state = getState();
        const hasCapability = selectUserHasCapability(
            state,
            Capability.CAP_FLEETAPP_VIEW_FLEETS
        );
        const hasAiGradingCapability = selectUserHasCapability(
            state,
            Capability.CAP_FLEETAPP_AI_GRADING
        );
        const userToken = selectUserToken(state);
        
        if (hasCapability && userToken) {
            dispatch(setInspectionAndGradeDataLoading(true));
            dispatch(setInspectionAndGradeDataError(false));

            try {
                const fleetDataItems = selectFleetDataItems(state);
                const imos = fleetDataItems.map((fleetDataItem) => fleetDataItem.vessel.imo);

                const promises = [
                    getInspectionAndGradeData(userToken)
                ];

                if (hasAiGradingCapability) {
                    promises.push(getInternalGradeData(imos.join(",")));
                }                    

                const results = await Promise.all(promises);
                const inspections = results[0];
                const internalGrades = hasAiGradingCapability ? results[1] : [];

                const inspectionData = [];

                if (inspections) {
                    const userDetails = selectUserDetails(state);

                    for (const key in inspections) {
                        const inspection = inspections[key];
                        const vesselData = fleetDataItems.find(item => String(item.vessel.imo) === inspection.shipImo);

                        if (vesselData) {
                            inspections[key].vessel = vesselData.vessel;
                            inspections[key].inspection = inspection.inspection;

                            const item = {
                                inspection: inspection,
                                vessel: vesselData.vessel,
                                scoreTrends: inspection.scoreTrends,
                                scoreHistory: filterScoreHistoryByType(inspection.scoreHistory) ?? [],
                            } as FleetDataItem;

                            inspectionData.push(item);
                        }
                    }

                    const inspectionAndGradeData = {fleetDataItems: inspectionData};

                    const mappedInspections = mapScheduledInspections(
                        inspectionAndGradeData as InspectionAndGradeDataResponse,
                        userDetails
                    );

                    dispatch(
                        setInspectionAndGradeData(mappedInspections)
                    );
                }

                if (internalGrades) {
                    dispatch(setInternalGradeData(internalGrades as InternalGradeData[]));
                }
            } catch (e) {
                console.error(e);
                dispatch(setInspectionAndGradeDataError(true));
                dispatch(setInspectionAndGradeData({ fleetDataItems: [] }));
                dispatch(setInternalGradeData([]));
            } finally {
                dispatch(setInspectionAndGradeDataLoading(false));
            }
        }

        return true;
    };

export const toggleStatsTiles = (): AppThunk => async (dispatch, getState) => {
    const state = getState();
    const showStatsTilesPanel = selectShowStatsTilesPanel(state);
    const userDetails = selectUserDetails(state);

    sendGAEvent(ANALYTICS_BUTTON_CLICK, FLEET_STATS_TOGGLE_CLICK);

    dispatch(setShowStatsTilesPanel(!showStatsTilesPanel));

    if (userDetails?.userName) {
        const userKey = userDetails.userName;
        const key = "toggleStatsTiles";
        storeUserSessionStorageItem(userKey, key, !showStatsTilesPanel);
    }
};

export const setupFleetDataWithInspections =
    (): AppThunk<Promise<boolean>> =>
    async (dispatch, getState) => {
        const state = getState();
        const hasAiGradingCapability = selectUserHasCapability(
            state,
            Capability.CAP_FLEETAPP_AI_GRADING
        );
        const { data, inspectionAndGradeData, internalGradeData } = selectFleet(state);

        let fleetData = cloneDeep(data.fleetDataItems);
        let inspectionGradeItems = cloneDeep(
            inspectionAndGradeData.fleetDataItems
        );
        let internalGrades = cloneDeep(internalGradeData);


        mutateFleetDataWithInspections(
            fleetData,
            inspectionGradeItems,
            internalGrades,
            hasAiGradingCapability
        );

        addCustomFieldFleetFiltersToData(fleetData);

        dispatch(setFleetDataItems(fleetData));

        return true;
    };

export const setupDistinctFleetData =
    (): AppThunk => async (dispatch, getState) => {
        const state = getState();
        const fleetDataItems = selectFleetDataItems(state);

        const vessels: string[] = [];
        const types: string[] = [];
        const managers: string[] = [];
        const countries: string[] = [];
        const flags: string[] = [];

        fleetDataItems.forEach((fleetDataItem) => {
            vessels.push(fleetDataItem.vessel.vesselName);
            types.push(fleetDataItem.vessel.type);
            managers.push(fleetDataItem.vessel.technicalManager);
            countries.push(fleetDataItem.vessel.countryOfBuild);
            flags.push(fleetDataItem.vessel.flag);
            vessels.push(fleetDataItem.vessel.vesselName);
        });

        dispatch(setDistinctVessels(getDistinctItems(vessels)));
        dispatch(setDistinctVesselTypes(getDistinctItems(types)));
        dispatch(setDistinctTechnicalManagers(getDistinctItems(managers)));
        dispatch(setDistinctCountriesOfBuild(getDistinctItems(countries)));
        dispatch(setDistinctFlags(getDistinctItems(flags)));

        const distinctVesselsWithCount = Array.from(
            new Set(toVesselTypeWithCount(fleetDataItems))
        )
            .filter(Boolean)
            .sort((a, b) => a.vesselType.localeCompare(b.vesselType));
        dispatch(setDistinctVesselsWithCount(distinctVesselsWithCount));
    };

export const setupFleetData =
    (): AppThunk<Promise<boolean>> => async (dispatch, getState) => {
        const state = getState();
        const { data, inspectionAndGradeData } = selectFleet(state);
        const userDetails = selectUserDetails(state);

        FilterService.register(
            `custom_${FleetTableFields.RANK}`,
            (value: any, filter: any) =>
                customRankFilter(
                    value,
                    filter,
                    data.fleetDataItems.filter((x) => x.gradeFilterField).length
                )
        );

        if (userDetails) {
            const graphContextFilter = getUserSessionStorageItem(
                "user",
                "graphContextFilter"
            );
            dispatch(
                setGraphContextFilter(
                    graphContextFilter ? Number(graphContextFilter) : 0
                )
            );

            // Note: loadGlobalFilterStateFromLocalStorage() is now called earlier via loadGlobalFilterStateEarly()
            await dispatch(setupFleetDataWithInspections());
            dispatch(setupDistinctFleetData());

            const marketDataFilteringOptionsResponse = userDetails.filterData;
            dispatch(
                setMarketDataFilteringOptions(
                    marketDataFilteringOptionsResponse
                )
            );
            dispatch(
                setupAdvancedFilters(
                    inspectionAndGradeData,
                    marketDataFilteringOptionsResponse
                )
            );
            dispatch(getStoredAdvancedFiltersState());
        }

        dispatch(setLoading(false));

        return true;
    };


export const setupFleetStatsData =
    (): AppThunk => async (dispatch, getState) => {
        const state = getState();
        const inspectionAndGradeData = selectInspectionAndGradeData(state);
        const userDetails = selectUserDetails(state);
        const defects = selectDefects(state);
        const { subgradeData, subgradeBackingData } = selectFleetGraph(state);

        if (
            inspectionAndGradeData &&
            userDetails &&
            defects &&
            subgradeData &&
            subgradeBackingData
        ) {
            const fleetStatistics = calculateFleetStatistics(
                inspectionAndGradeData.fleetDataItems,
                subgradeData,
                subgradeBackingData,
                defects
            );

            const inspectionsCompletedInContractYear =
                await getInspectionsCompletedInContractYear();
            if (inspectionsCompletedInContractYear) {
                fleetStatistics.unshift(inspectionsCompletedInContractYear);
            }

            dispatch(
                setStatTileConfigs(fleetStatistics.slice(0, MAX_VISIBLE_TILES))
            );
        } else {
            dispatch(setStatTileConfigs([]));
        }
    };


const filterScoreHistoryByType = (scoreHistory: FleetDataItem[]) => {
    if (!scoreHistory || scoreHistory.length === 0) {
        return [];
    }
    // If there are multiple inspections of the same type, filter to keep only the latest one
    const latestInspections: Record<string, FleetDataItem> = {};
    scoreHistory.forEach((item) => {
        const inspectionType = item.inspectionType;
        if (!latestInspections[inspectionType] || new Date(item.lastInspected) > new Date(latestInspections[inspectionType].lastInspected)) {
            latestInspections[inspectionType] = item;
        }
    });

    return Object.values(latestInspections);
}